<template>
  <div class="list-header">
    <div class="tabs-row">
      <GamTab :tabs="tabItems" @update="onTabItemClick" />
    </div>

    <div v-if="false" class="filtering">
      <div class="filter-actions">
        <GamSwitch v-bind="getSwitch" v-model:checked="switchValue" class="switch" />
        <GamButton v-bind="getFilterButton" />
        <GamButton v-bind="getSearchButton" />
      </div>

      <transition name="slide">
        <div v-if="showSearchInput" class="search-input">
          <GamInputSearch v-bind="getSearchInput" />
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RouterNameType, TabItemType } from '@/core/data/config/uiConfig.interface';
import type { BaseUserDto } from '@/core/data/user/user.interface';
import { gambits } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import type { GetPlayerTableFilters } from '@/core/network/http/httpClient.interface';
import { useFilterStore } from '@/stores/filter.store';
import { useMapStore } from '@/stores/map.store';
import GamButton from '@/views/components/GamButton.vue';
import GamInputSearch from '@/views/components/GamInputSearch.vue';
import GamSwitch from '@/views/components/GamSwitch.vue';
import GamTab from '@/views/components/gam-tab/GamTab.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamInputName } from '@/views/composables/constants/components/gamInput.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { GamSwitchPosition, GamSwitchValueType } from '@/views/composables/constants/components/gamSwitch.constants';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamInputSearchType } from '@/views/composables/models/components/GamInput';
import type { GamSwitchType } from '@/views/composables/models/components/GamSwitch';
import type { GamTabItemType } from '@/views/composables/models/components/GamTab';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const listId = GamListId.GAMBITS;
const filterStore = useFilterStore<GetPlayerTableFilters>(listId)();
const { listTabItems } = storeToRefs(filterStore);
const mapStore = useMapStore();
const { currentLocation, isLocationValid } = storeToRefs(mapStore);
const router = useRouter();
const route = useRoute();

const switchValue = ref<boolean>(false);
const showSearchInput = ref<boolean>(false);

onBeforeMount(() => {
  filterStore.setListTabItems(gambits.configService.getGambitsViewTabItems());
});

const onTabItemClick = (name: TabItemType) => {
  if (filterStore.getActiveTab() === name || (!filterStore.getActiveTab() && name === TabItemType.ALL)) return;

  if (name === TabItemType.ALL) {
    let newQuery = { filter: undefined };
    if (isLocationValid.value) {
      newQuery = { ...newQuery, ...currentLocation.value };
    }

    router.push({
      name: RouterNameType.GAMBITS,
      params: { id: undefined },
      query: { ...route.query, ...newQuery },
    });
  } else {
    router.push({
      name: RouterNameType.GAMBITS,
      params: { id: undefined },
      query: { ...route.query, lat: undefined, lng: undefined, filter: name },
    });
  }
};

const tabItems = computed((): GamTabItemType[] => {
  return [
    {
      name: 'mine' as TabItemType,
      displayName: 'My Gambits',
      position: 1,
      isActive: listTabItems.value?.find((tab) => tab.name === 'mine')?.isActive,
    },
    {
      name: 'following' as TabItemType,
      displayName: 'Following',
      position: 2,
      isActive: listTabItems.value?.find((tab) => tab.name === 'following')?.isActive,
    },
  ];
});

const getSwitch = computed((): GamSwitchType => {
  return {
    label: 'gambits.general.header.switch.needCoPlayer.label',
    name: '',
    type: GamSwitchValueType.SWITCH,
    position: GamSwitchPosition.LEFT,
  };
});

const getSearchButton = computed((): GamButtonType => {
  return {
    leftIcon: GamIconName.SEARCH,
    isIconOnly: true,
    size: GamButtonSize.LARGE,
    variant: GamButtonVariant.TERTIARY,
    isIconWhite: true,
    onClick: () => {
      showSearchInput.value = !showSearchInput.value;
    },
  };
});

const getFilterButton = computed((): GamButtonType => {
  return {
    leftIcon: GamIconName.FILTER,
    isIconOnly: true,
    size: GamButtonSize.LARGE,
    variant: GamButtonVariant.TERTIARY,
    isIconWhite: true,
  };
});

const getSearchInput = computed((): GamInputSearchType<BaseUserDto> => {
  return {
    id: GamListId.GAMBITS,
    input: {
      name: GamInputName.SEARCH,
      placeholder: 'gambits.general.search.placeholder',
      leftIcon: GamIconName.SEARCH,
    },
    selectFirstItem: !isMobile.value,
  };
});
</script>

<style scoped lang="scss">
.tabs-row {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  align-self: stretch;
  gap: var(--spacing-menu);

  .gam-button {
    flex-shrink: 0;
  }
}

.filtering {
  display: flex;
  width: 100%;
  align-items: center;
  flex-direction: column;
  gap: var(--spacing-xs);
  transition: height var(--transition);
  position: relative;
}

.filter-actions {
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: var(--spacing-xs);
}

.search-input {
  width: 100%;
  top: 0;
  position: relative;
}
</style>
